import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import {
  BdcEdiExport,
  BdcEdiExportType,
  BdcEdiFileType,
  BdcEdiPreview,
  BdcEdiShipment,
  createBdcEdiExport,
  downloadBdcEdiExport,
  listBdcEdiExports,
  listBdcEdiShipments,
  previewBdcEdiExport,
} from "../../../services/bdcEdi";
import { featureAccess } from "../../../permissions/access";
import { saveBlobResponse } from "../../../utils/downloadBlob";
import { useDebounce } from "../../../utils/hooks/useDebounce";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import { BdcApiError, readBdcError } from "../apiError";
import BranchChip from "../Branch/BranchChip";
import BranchSelector from "../Branch/BranchSelector";
import TestModeBanner from "../Branch/TestModeBanner";
import { useBdcBranchSelection, useBdcBranches } from "../Branch/useBdcBranches";
import { BDC_CREDIT_NOTES_PATH, bdcShipmentLink } from "../constants";
import { parseSelectParam } from "../Documents/documents";
import { baht, pcs, roundLabel, thaiDate, thaiDateTime, todayBkk } from "../format";
import StatusChip from "../StatusChip";
import { CARD_SX, SCROLL_SX, TABLE_SX } from "../styles";
import Tag from "../Tag";
import { useBdcDcs } from "../useBdcDcs";
import { EDI_FILE_LABEL, formatBytes, invoiceRange, isSelectable, rowNote, selectionProblem } from "./edi";

type DateMode = "order" | "delivery";
const ALL = "all";
const HISTORY_LIMIT = 20;
const FILE_TYPES: BdcEdiFileType[] = ["do", "invoice"];

const fileContent = (file: {
  type: BdcEdiExportType;
  po_numbers: string[];
  record_count: number;
  invoice_no_from: string | null;
  invoice_no_to: string | null;
  note_nos?: string[];
}) => {
  if (file.type === "invoice") return `${pcs(file.record_count)} INV ${invoiceRange(file.invoice_no_from, file.invoice_no_to)}`;
  // the RETSEV file of the CN screen
  if (file.type === "cn") return `${pcs(file.record_count)} CN${file.note_nos?.length ? ` ${file.note_nos.join(", ")}` : ""}`;
  return `${pcs(file.po_numbers.length)} PO · ${pcs(file.record_count)} ร้าน`;
};

function FailureAlert({ failure, onClose }: Readonly<{ failure: BdcApiError; onClose: () => void }>) {
  return (
    <Alert severity="error" onClose={onClose} data-testid="bdc-edi-error">
      {failure.message}
      {failure.poNumbers.length > 0 && <div>PO: {failure.poNumbers.join(", ")}</div>}
    </Alert>
  );
}

/**
 * BDC · ไฟล์ส่ง 7-11 (W10) — tick the day's shipments → preview the DO_*.txt + Invoice_*.txt
 * (names, counts, INV range, which POs lock) → create → both files download as stored (TIS-620,
 * untouched) for DESKTOP EAI. The Invoice file's POs become รอนำส่ง 7-11 and lock (a mistake after
 * this is a CN). The history re-downloads any file.
 */
export default function BdcEdi() {
  const role = useUserRole();
  const canCreate = featureAccess.bdcDesk(role);
  const [params, setParams] = useSearchParams();
  const { branches, ready: branchesReady } = useBdcBranches();
  const branch = useBdcBranchSelection(branches);
  const { dcs } = useBdcDcs();

  const mode: DateMode = params.get("delivery_date") ? "delivery" : "order";
  const date = params.get(mode === "order" ? "order_date" : "delivery_date") || todayBkk();
  const dcCode = params.get("dc_code") || ALL;
  const status = params.get("status") || ALL;

  const [rows, setRows] = useState<BdcEdiShipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(parseSelectParam(params.get("select"))));
  const [types, setTypes] = useState<Set<BdcEdiFileType>>(() => new Set(FILE_TYPES));
  const [preview, setPreview] = useState<BdcEdiPreview | null>(null);
  const [busy, setBusy] = useState<"preview" | "create" | null>(null);
  const [failure, setFailure] = useState<BdcApiError | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const [history, setHistory] = useState<BdcEdiExport[]>([]);
  const [historyCount, setHistoryCount] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyType, setHistoryType] = useState<string>(ALL);
  const [searchText, setSearchText] = useState("");
  const search = useDebounce(searchText, 400);
  const [downloading, setDownloading] = useState<string | null>(null);

  const loadShipments = useCallback(async () => {
    if (!branchesReady) return;
    setLoading(true);
    try {
      const {
        data: { data },
      } = await listBdcEdiShipments({
        ...(mode === "order" ? { order_date: date } : { delivery_date: date }),
        ...(dcCode !== ALL ? { dc_code: dcCode } : {}),
        ...(status !== ALL ? { status: status as "do_confirmed" | "invoiced" | "queued_7_11" } : {}),
        ...(branch.companyId ? { company_id: branch.companyId } : {}),
      });
      const list = data?.rows ?? [];
      setRows(list);
      setSelected(
        (current) => new Set(list.filter((r) => isSelectable(r) && current.has(r.shipment_uuid)).map((r) => r.shipment_uuid)),
      );
    } catch (e) {
      setRows([]);
      setFailure(readBdcError(e, "โหลดรายการ PO ไม่สำเร็จ"));
    } finally {
      setLoading(false);
    }
  }, [mode, date, dcCode, status, branch.companyId, branchesReady]);

  const loadHistory = useCallback(async () => {
    if (!branchesReady) return;
    try {
      const {
        data: { data },
      } = await listBdcEdiExports({
        page: historyPage + 1,
        limit: HISTORY_LIMIT,
        ...(historyType !== ALL ? { type: historyType as BdcEdiExportType } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(branch.companyId ? { company_id: branch.companyId } : {}),
      });
      setHistory(data?.rows ?? []);
      setHistoryCount(data?.count ?? 0);
    } catch {
      setHistory([]);
      setHistoryCount(0);
    }
  }, [historyPage, historyType, search, branch.companyId, branchesReady]);

  useEffect(() => {
    loadShipments();
  }, [loadShipments]);
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    next.delete("select");
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === ALL) next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };
  const handleMode = (event: ChangeEvent<HTMLInputElement>) => {
    const nextMode = event.target.value as DateMode;
    update(nextMode === "order" ? { order_date: date, delivery_date: null } : { delivery_date: date, order_date: null });
  };
  const handleDate = (value: Dayjs | null) => {
    if (!value || !value.isValid()) return;
    update({ [mode === "order" ? "order_date" : "delivery_date"]: value.format("YYYY-MM-DD") });
  };

  const selectable = useMemo(() => rows.filter(isSelectable), [rows]);
  const picked = rows.filter((r) => selected.has(r.shipment_uuid));
  const allPicked = selectable.length > 0 && picked.length === selectable.length;
  const problem = selectionProblem(rows, selected, types);
  const request = {
    shipment_uuids: picked.map((r) => r.shipment_uuid),
    types: FILE_TYPES.filter((t) => types.has(t)),
  };

  const toggle = (uuid: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  const toggleType = (type: BdcEdiFileType) =>
    setTypes((current) => {
      const next = new Set(current);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });

  const download = async (file: Pick<BdcEdiExport, "uuid" | "file_name">) => {
    setDownloading(file.uuid);
    try {
      saveBlobResponse(await downloadBdcEdiExport(file.uuid), file.file_name);
    } catch {
      // reported by the interceptor (errorMsgByStatus)
    } finally {
      setDownloading(null);
    }
  };

  const runPreview = async () => {
    if (problem) return;
    setBusy("preview");
    setFailure(null);
    setDone(null);
    try {
      const {
        data: { data },
      } = await previewBdcEdiExport(request);
      setPreview(data);
    } catch (e) {
      setFailure(readBdcError(e, "ตรวจไฟล์ส่ง 7-11 ไม่สำเร็จ"));
    } finally {
      setBusy(null);
    }
  };

  const create = async () => {
    setBusy("create");
    setFailure(null);
    try {
      const {
        data: { data },
      } = await createBdcEdiExport(request);
      setPreview(null);
      setSelected(new Set());
      for (const file of data?.rows ?? []) {
        // one after another: the browser keeps both downloads
        // eslint-disable-next-line no-await-in-loop
        await download(file);
      }
      setDone(
        `สร้าง ${pcs(data?.count ?? 0)} ไฟล์แล้ว${
          data?.queued_po_numbers.length ? ` · รอนำส่ง 7-11: ${data.queued_po_numbers.join(", ")}` : ""
        }`,
      );
    } catch (e) {
      setPreview(null);
      setFailure(readBdcError(e, "สร้างไฟล์ส่ง 7-11 ไม่สำเร็จ"));
    } finally {
      setBusy(null);
      loadShipments();
      loadHistory();
    }
  };

  const dcOptions = branch.companyId ? dcs.filter((dc) => dc.company_id === branch.companyId) : dcs;

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box mb={2}>
        <Typography variant="h6" fontWeight={700}>
          BDC · ไฟล์ส่ง 7-11
        </Typography>
        <Typography variant="body2" color="text.secondary">
          เลือก PO → ดูไฟล์ที่จะสร้าง → สร้างไฟล์ DO + Invoice → download แล้วอัปโหลดผ่าน DESKTOP EAI ด้วยมือ
        </Typography>
      </Box>

      <Stack gap={2} sx={TABLE_SX}>
        <Card sx={{ ...CARD_SX, p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
              <BranchSelector branches={branches} value={branch.selected} onChange={branch.select} />
              <TextField select size="small" label="กรองตาม" value={mode} onChange={handleMode} sx={{ width: 140 }}>
                <MenuItem value="order">วันที่สั่ง</MenuItem>
                <MenuItem value="delivery">วันส่ง</MenuItem>
              </TextField>
              <DatePicker
                label={mode === "order" ? "วันที่สั่ง" : "วันส่ง"}
                value={dayjs(date)}
                format="DD/MM/YYYY"
                onChange={handleDate}
                slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
              />
              <TextField select size="small" label="DC" value={dcCode} onChange={(e) => update({ dc_code: e.target.value })} sx={{ minWidth: 180 }}>
                <MenuItem value={ALL}>ทุก DC</MenuItem>
                {dcOptions.map((dc) => (
                  <MenuItem key={dc.dc_code} value={dc.dc_code}>
                    {dc.dc_code} {dc.dc_name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="สถานะ" value={status} onChange={(e) => update({ status: e.target.value })} sx={{ width: 170 }}>
                <MenuItem value={ALL}>ทุกสถานะ</MenuItem>
                <MenuItem value="do_confirmed">ยืนยัน DO</MenuItem>
                <MenuItem value="invoiced">ออก INV แล้ว</MenuItem>
                <MenuItem value="queued_7_11">รอนำส่ง 7-11</MenuItem>
              </TextField>
            </Stack>
          </LocalizationProvider>
        </Card>

        <TestModeBanner branches={branch.viewed} />
        {loading && <LinearProgress />}
        {failure && <FailureAlert failure={failure} onClose={() => setFailure(null)} />}
        {done && (
          <Alert severity="success" onClose={() => setDone(null)} data-testid="bdc-edi-done">
            {done}
          </Alert>
        )}

        <Card sx={{ ...CARD_SX, p: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Typography fontWeight={600}>PO ของวัน · {thaiDate(date)}</Typography>
          <Box sx={{ ...SCROLL_SX, maxHeight: 440 }}>
            <table className="bdc-t" data-testid="bdc-edi-candidates">
              <thead>
                <tr>
                  <th>
                    <Checkbox
                      size="small"
                      checked={allPicked}
                      indeterminate={picked.length > 0 && !allPicked}
                      disabled={selectable.length === 0}
                      onChange={(e) => setSelected(new Set(e.target.checked ? selectable.map((r) => r.shipment_uuid) : []))}
                      slotProps={{ input: { "aria-label": "เลือกทั้งหมด" } }}
                    />
                  </th>
                  <th>สาขา</th>
                  <th>PO</th>
                  <th>WB / รอบ</th>
                  <th>สถานะ</th>
                  <th>INV</th>
                  <th className="n">ร้าน</th>
                  <th className="n">ชิ้นส่ง</th>
                  <th className="n">รวม VAT</th>
                  <th>ไฟล์ DO</th>
                  <th>ไฟล์ Invoice</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const note = rowNote(row);
                  return (
                    <tr key={row.shipment_uuid}>
                      <td>
                        <Checkbox
                          size="small"
                          disabled={!isSelectable(row)}
                          checked={selected.has(row.shipment_uuid)}
                          onChange={() => toggle(row.shipment_uuid)}
                          slotProps={{ input: { "aria-label": `เลือก ${row.po_number}` } }}
                        />
                      </td>
                      <td>
                        <BranchChip branches={branches} companyId={row.company_id} branchCode={row.branch_code} />
                      </td>
                      <td className="mono">
                        <RouterLink to={bdcShipmentLink(row.po_number, "inv")}>{row.po_number}</RouterLink>
                      </td>
                      <td>
                        <span className="dc">{row.dc_code}</span> / {roundLabel(row.kind, row.round)}
                      </td>
                      <td>
                        <StatusChip status={row.status} />
                      </td>
                      <td className="mono">{row.invoice_no ?? "—"}</td>
                      <td className="n">{row.kind === "X" ? "—" : pcs(row.store_count)}</td>
                      <td className="n">{pcs(row.delivered_pieces)}</td>
                      <td className="n">{row.grand_total === null ? "—" : baht(row.grand_total)}</td>
                      <td className="sub">{row.do_exported_at ? thaiDateTime(row.do_exported_at) : "—"}</td>
                      <td className="sub">{row.invoice_exported_at ? thaiDateTime(row.invoice_exported_at) : "—"}</td>
                      <td>{note && <Tag tone={row.is_test_mode ? "warn" : "mut"}>{note}</Tag>}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={12} className="sub" style={{ textAlign: "center", padding: 24 }}>
                      ไม่มี PO ที่ยืนยัน DO แล้วของวันนี้
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1.25} flexWrap="wrap">
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2">ไฟล์:</Typography>
              {FILE_TYPES.map((type) => (
                <FormControlLabel
                  key={type}
                  control={<Checkbox size="small" checked={types.has(type)} onChange={() => toggleType(type)} />}
                  label={EDI_FILE_LABEL[type]}
                />
              ))}
            </Stack>
            <Button
              variant="contained"
              color="warning"
              disabled={!canCreate || Boolean(problem)}
              loading={busy === "preview"}
              onClick={runPreview}
            >
              ดูไฟล์ที่จะสร้าง ({pcs(picked.length)} PO)
            </Button>
          </Stack>
          <Typography className="sub">
            {picked.length > 0 && problem ? problem : "ไฟล์ Invoice ทำให้ PO เป็นรอนำส่ง 7-11 และล็อก — แก้ยอด/ยกเลิก INV ไม่ได้ ผิดพลาดต้องทำ CN"}
          </Typography>
        </Card>

        <Card sx={{ ...CARD_SX, p: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
            <Typography fontWeight={600}>ประวัติไฟล์ · download แล้วอัปโหลดผ่าน DESKTOP EAI ด้วยมือ</Typography>
            <Box sx={{ flex: 1 }} />
            <TextField select size="small" label="ชนิด" value={historyType} onChange={(e) => { setHistoryType(e.target.value); setHistoryPage(0); }} sx={{ width: 130 }}>
              <MenuItem value={ALL}>ทั้งหมด</MenuItem>
              <MenuItem value="do">DO</MenuItem>
              <MenuItem value="invoice">Invoice</MenuItem>
              <MenuItem value="cn">CN (RETSEV)</MenuItem>
            </TextField>
            <TextField
              size="small"
              placeholder="ชื่อไฟล์ / PO / INV"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setHistoryPage(0);
              }}
              sx={{ width: 220 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>
          <Box sx={{ overflowX: "auto" }}>
            <table className="bdc-t" data-testid="bdc-edi-history">
              <thead>
                <tr>
                  <th>เวลา</th>
                  <th>ไฟล์</th>
                  <th>ข้อมูลในไฟล์</th>
                  <th className="n">ขนาด</th>
                  <th>ผู้สร้าง</th>
                  <th>EAI profile</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {history.map((file) => (
                  <tr key={file.uuid}>
                    <td>{thaiDateTime(file.created_at)}</td>
                    <td className="mono">{file.file_name}</td>
                    <td>{fileContent(file)}</td>
                    <td className="n sub">{formatBytes(file.bytes)}</td>
                    <td className="sub">{file.created_by_name ?? ""}</td>
                    <td className="mono">{file.eai_profile}</td>
                    <td>
                      <Button size="small" variant="outlined" loading={downloading === file.uuid} onClick={() => download(file)}>
                        download
                      </Button>
                    </td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr>
                    <td colSpan={7} className="sub" style={{ textAlign: "center", padding: 16 }}>
                      ยังไม่มีไฟล์
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
          {historyCount > HISTORY_LIMIT && (
            <TablePagination
              component="div"
              count={historyCount}
              page={historyPage}
              rowsPerPage={HISTORY_LIMIT}
              rowsPerPageOptions={[HISTORY_LIMIT]}
              onPageChange={(_e, next) => setHistoryPage(next)}
            />
          )}
          <Typography className="sub">
            ชื่อไฟล์และรูปแบบเหมือนโปรแกรมเดิม (TIS-620) · DO = 1 ส่วนต่อร้าน · Invoice = 1 ส่วนต่อ PO · CN (RETSEV)
            สร้างจากหน้า{" "}
            <RouterLink to={BDC_CREDIT_NOTES_PATH}>ใบลดหนี้ (CN)</RouterLink> · download ซ้ำไม่ล็อกซ้ำ
          </Typography>
        </Card>
      </Stack>

      <Dialog open={preview !== null} onClose={busy ? undefined : () => setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>สร้างไฟล์ส่ง 7-11 · {pcs(preview?.shipment_count ?? 0)} PO</DialogTitle>
        <DialogContent sx={TABLE_SX} data-testid="bdc-edi-preview">
          <table className="bdc-t">
            <thead>
              <tr>
                <th>ไฟล์</th>
                <th>ข้อมูลในไฟล์</th>
                <th className="n">บรรทัด</th>
                <th className="n">ขนาด</th>
                <th>EAI profile</th>
              </tr>
            </thead>
            <tbody>
              {preview?.files.map((file) => (
                <tr key={file.file_name}>
                  <td className="mono">{file.file_name}</td>
                  <td>
                    {fileContent(file)}
                    <div className="sub">PO: {file.po_numbers.join(", ")}</div>
                  </td>
                  <td className="n">{pcs(file.line_count)}</td>
                  <td className="n">{formatBytes(file.bytes)}</td>
                  <td className="mono">{file.eai_profile}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview && preview.skipped_types.length > 0 && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              ไม่มีไฟล์ {preview.skipped_types.map((t) => (t === "do" ? "DO" : "Invoice")).join(", ")} — PO ที่เลือกไม่มีข้อมูลสำหรับไฟล์นี้
            </Alert>
          )}
          {preview && preview.locks_po_numbers.length > 0 ? (
            <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="bdc-edi-locks">
              <AlertTitle>PO {pcs(preview.locks_po_numbers.length)} รายการจะเป็น "รอนำส่ง 7-11" และล็อก</AlertTitle>
              {preview.locks_po_numbers.join(", ")} — แก้ยอดส่งหรือยกเลิก INV ไม่ได้อีก ผิดพลาดต้องทำ CN
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              ไม่มี PO ถูกล็อก (สร้างเฉพาะไฟล์ DO)
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)} disabled={busy === "create"}>
            ยกเลิก
          </Button>
          <Button variant="contained" color="warning" loading={busy === "create"} onClick={create}>
            {preview?.locks_po_numbers.length ? "สร้างไฟล์และล็อก" : "สร้างไฟล์"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
